import { img } from "@/utils/projectImages";

// All photos live in src/assets/projects (including subfolders) and are
// resolved by filename without extension.
export const images = {
  heroGathering: img("hero-gathering"),
  kitchenTerrazzo: img("kitchen-terrazzo"),
  nookSage: img("nook-sage"),
  livingCurvedSofa: img("living-curved-sofa"),
  diningDarkOak: img("dining-dark-oak"),
  livingSheerBlinds: img("living-sheer-blinds"),
  bedroomArch: img("bedroom-arch"),
  kitchenArchTerracotta: img("kitchen-arch-terracotta"),
  livingTvConsole: img("living-tv-console"),
  livingHallWhite: img("living-hall-white"),

  // Wide, high-resolution shots used for the page-hero banners.
  hero1: img("hero-1"),
  hero2: img("hero-2"),
  hero3: img("hero-3"),
  hero4: img("hero-4"),
};

export const heroImage = images.heroGathering;

/**
 * Projects, each broken into sections. Copy is EIKA's own, lightly edited for
 * grammar only — the voice and specifics are theirs.
 */
export const projects = [
  // Most recent work first.
  {
    slug: "dink-enclave",
    name: "DINK Enclave",
    category: "HDB",
    summary:
      "A private enclave of chalky limewashed texture and open curves, enlarged and made flexible for a young couple.",
    cover: img("dinkenclave_cover1"),
    sections: [
      {
        title: "Living",
        lead: "Stepping inside the main living hall feels like arriving at your private enclave, filled with chalky, muddled textures in subtle reflections.",
        body: "The open curved ends of the sofa naturally encourage conversation among guests, facing the extendable dining table as sunlight filters through the full-height S-fold curtains onto the limewashed surfaces.",
        images: [
          // The two cover shots lead — only the first two are shown as tiles.
          img("dinkenclave_cover1"),
          img("dinkenclave_cover2"),
          img("dinkenclave-9"),
          img("dinkenclave-13"),
          img("dinkenclave-10"),
          img("dinkenclave-5"),
        ],
      },
      {
        title: "Kitchen",
        lead: "From the unblocked service yard that opens up to the kitchen, a linear workflow spans the entire length of the closed kitchen.",
        body: "The chimney hood grounds the kitchen in a contrasting full-height backsplash niche, while the lime-washed surfaces continue from the living area. A server window hatch then offers the flexibility of opening the space over to the dining whenever desired.",
        images: [
          img("dinkenclave-1"),
          img("dinkenclave-8"),
          img("dinkenclave-2"),
          img("dinkenclave-4"),
          img("dinkenclave-6"),
        ],
      },
      {
        title: "Bedrooms",
        lead: "The bedrooms become a space to pause, reset and reconnect with their surroundings, transforming as your needs shift.",
        body: "We enlarged the space for the young couple and crafted L-shaped tri-fold doors that open up completely, concealing the bathroom and wardrobe graciously. A Murphy bed is available too, whenever guests feel like staying over.",
        images: [
          img("dinkenclave-12"),
          img("dinkenclave-11"),
          img("dinkenclave-3"),
          img("dinkenclave-7"),
        ],
      },
    ],
  },
  {
    slug: "the-soft-grove",
    name: "The Soft Grove",
    category: "HDB",
    summary:
      "A home of round edges and diffused light, designed for a young family caring for an elderly parent living with dementia.",
    cover: images.kitchenTerrazzo,
    sections: [
      {
        title: "Living",
        lead: "Round edges, diffused light and a serene palette bring calm to this living space, inviting stillness and slow living.",
        body: "Sphere sconces brighten the house, casting warmth across every surface. Designed for a young family with an elderly parent living with dementia, where everyday rituals unfold with ease and intention.",
        images: [img("softgrove-4"), img("softgrove-2"), img("softgrove-5")],
      },
      {
        title: "Kitchen & Dining",
        lead: "Reimagined as an oasis of serenity, designed for slow mornings and long dinners with loved ones in this open kitchen.",
        body: "The peninsula island anchors the space for gatherings and baking sessions, while also acting as a divider between the dining and the kitchen. The dining table sits adjacent to the island, making it easy to serve daily meals.",
        images: [img("softgrove-7"), img("softgrove-8"), img("softgrove-9"), img("softgrove-6")],
      },
      {
        title: "Bathrooms",
        lead: "Every little corner of calm to start and end the day. Your morning and night routines deserve a space this inspiring.",
        body: "Cement-textured surfaces, earl grey granola terrazzo and round-edge patterned terracotta tiles are found throughout their home. Round edges, warm light and tactile serenity ultimately define this restful retreat.",
        images: [img("softgrove-1"), img("softgrove-3")],
      },
    ],
  },
  {
    slug: "oaks-nest",
    name: "Oak's Nest",
    category: "HDB",
    summary:
      "A light retirement sanctuary in layered oak and muted white greens, shaped around quiet comfort and reassurance.",
    cover: images.nookSage,
    sections: [
      {
        title: "Living",
        lead: "A light retirement sanctuary with a variety of oak tones paired with muted white greens, for peaceful and quiet comfort.",
        body: "An integrated storage TV console and window settee form a gentle invitation at a standardised height. Half-wall oak fluted panels establish visual hierarchy and physical support for the elderly, giving a sense of calm, order and reassurance.",
        images: [img("oaknest-12"), img("oaknest-5"), img("oaknest-10")],
      },
      {
        title: "Kitchen",
        lead: "As soon as you reach the entrance of the kitchen, a double wall sconce creates a unique statement that greets you with a welcoming glow.",
        body: "While retaining the light oak textures, an accent wall doubles up to light the peninsula dining island and partially hide clutter from the semi-open kitchen.",
        images: [img("oaknest-3"), img("oaknest-4"), img("oaknest-9")],
      },
      {
        title: "Dining & Study",
        lead: "Where dining is the living area, and everyday moments unfold at home this seamlessly.",
        body: "From the dining nook enclosure to the compact work desk cum bedroom, the repetitive straight lines of the fluted panels and honeycomb blinds promote a flow state that is therapeutic and mindful, all while keeping the space clutter-free.",
        images: [img("oaknest-11"), img("oaknest-8"), img("oaknest-1"), img("oaknest-6")],
      },
      {
        // No copy supplied by EIKA for this room — photos only, rather than
        // inventing a description.
        title: "Bathroom",
        images: [img("oaknest-2"), img("oaknest-7")],
      },
    ],
  },
];

export default projects;
